import { Auth } from "aws-amplify";
import { jwtDecode, JwtPayload } from "jwt-decode";
import { extractStructuredData } from "../../Content/verify_role";
import { parsData } from "../../Content/verify_role";

/**
 * Interfaz que extiende JwtPayload para incluir propiedades personalizadas.
 * Esta extensión permite acceder a propiedades específicas definidas en el token, como `custom:roles`.
 */
interface CustomJwtPayload extends JwtPayload {
  "custom:roles"?: string; // Roles personalizados almacenados en el token
  email?: string;
}

/**
 * Obtiene datos del usuario autenticado en Cognito, procesa el token para extraer roles personalizados
 * y retorna los datos relevantes del usuario junto con los roles y los tokens.
 *
 * @returns Un objeto con información del usuario, sus roles y los tokens de sesión.
 * @throws Error si ocurre algún problema al obtener los datos del usuario o procesar el token.
 */
export async function cognitoUserData() {
  /**
   * Decodifica un JWT y retorna sus datos en un formato legible.
   *
   * @param token - El token JWT a decodificar.
   * @returns Los datos decodificados del token o `null` si ocurre un error.
   */
  const decodeToken = (token: string): CustomJwtPayload | null => {
    try {
      const decoded = jwtDecode<CustomJwtPayload>(token);
      return decoded;
    } catch (error) {
      console.error("Error decoding token:", error);
      return null;
    }
  };

  try {
    // Obtiene el usuario autenticado actual desde Cognito
    const cognitoUser = await Auth.currentAuthenticatedUser();
    const session = await Auth.currentSession();
    // Verifica que los datos y los tokens estén disponibles
    if (!cognitoUser) {
      console.error("User data or tokens are not available");
    }
    // Extrae atributos del usuario
    const attributes = cognitoUser.attributes || {};
    const username = cognitoUser.username || "Unknown User"; // Nombre de usuario
    const email = attributes.email || "No email provided"; // Correo electrónico

    // Extrae los tokens de la sesión
    const idToken = session.getIdToken().getJwtToken(); // Token de identificación
    const accessToken = session.getAccessToken().getJwtToken(); // Token de acceso
    const refreshToken = session.getRefreshToken().getToken(); // Token de actualización

    let roles = null; // Variable para almacenar los roles procesados
    let email_user = "No email provided";

    if (idToken) {
      // Decodifica el token de identificación para extraer datos personalizados
      const decodedData = decodeToken(idToken);

      if (decodedData) {
        // Extrae y procesa los roles personalizados del token

        email_user = decodedData?.email || "No email provided";
        sessionStorage.setItem("email_user_c", email_user); // Guarda
        console.log(email_user);

        const customRoles = decodedData["custom:roles"]
          ?.replace(/ /g, "") // Elimina espacios en blanco
          .split(";"); // Divide los roles en una lista

        if (customRoles) {
          roles = parsData(extractStructuredData(customRoles)); // Procesa los roles
          // console.log("Datos procesados como roles:", roles);

          // Guarda los roles en el almacenamiento de sesión
          sessionStorage.setItem("user_roles_data", JSON.stringify(roles));
        } else {
          console.warn("No se encontraron roles en el token.");
        }
      }
    }

    // Retorna un objeto con los datos relevantes del usuario
    return {
      cognitoUser,
      username, // Nombre de usuario
      email, // Correo electrónico
      attributes, // Otros atributos del usuario
      roles, // Roles procesados
      tokens: {
        idToken, // Token de identificación
        accessToken, // Token de acceso
        refreshToken, // Token de actualización
      },
    };
  } catch (error: any) {
    // Manejo de errores: registra el error y lo lanza para manejarlo en otro lugar
    console.error(`Failed to fetch Cognito user data: ${error.message}`, error);

    throw error;
  }
}
